import { useState, useEffect, useCallback, useMemo } from "react";
import { Landmark, Link as LinkIcon, RefreshCw, Unlink, CheckCircle2, AlertTriangle, Download as DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const fmt = (v) => `£${Math.abs(Number(v)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function formatDateTime(iso) {
  if (!iso) return "Never";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function formatDateOnly(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

const consentStyles = {
  active: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", label: "Active" },
  expiring_soon: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", label: "Expiring Soon" },
  expired: { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", label: "Expired — Reconnect" },
};

function currentMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const categoryBadge = {
  income: "border-green-300 text-green-700 bg-green-50",
  expense: "border-gray-300 text-gray-600 bg-gray-50",
  debt_payment: "border-blue-300 text-blue-700 bg-blue-50",
  transfer: "border-amber-300 text-amber-700 bg-amber-50",
};

const CATEGORY_OPTIONS = [
  { value: "income", label: "Income" },
  { value: "expense", label: "Bill/Expense" },
  { value: "debt_payment", label: "Debt Payment" },
  { value: "transfer", label: "Transfer (exclude)" },
];

// Derive a short, stable pattern key from a bank description for rule-saving
function derivePattern(description) {
  if (!description) return "";
  // Strip common noise: leading numbers/dates, trailing amounts, reference codes
  let s = String(description).trim().toUpperCase();
  // Remove common date prefixes "15/04/2026" etc.
  s = s.replace(/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\s*/, "");
  // Take first 2 "word-like" tokens (letters/digits)
  const tokens = s.split(/\s+/).filter((t) => t && /[A-Z]/.test(t));
  return tokens.slice(0, 2).join(" ").slice(0, 30);
}

export default function BankConnection({ api }) {
  const [status, setStatus] = useState(null); // null=loading, {connected: bool, ...}
  const [syncing, setSyncing] = useState(false);
  const [preview, setPreview] = useState(null); // { transactions, provider_name, from_date, to_date }
  const [mode, setMode] = useState("append");
  const [importMonth, setImportMonth] = useState(currentMonthStr());
  const [importing, setImporting] = useState(false);
  const [rowSelections, setRowSelections] = useState({}); // idx -> bool
  const [syncFrom, setSyncFrom] = useState(daysAgo(90));
  const [syncTo, setSyncTo] = useState(today());
  const [sortDir, setSortDir] = useState("desc"); // "asc" or "desc" — preview date sort
  const [categoryOverrides, setCategoryOverrides] = useState({}); // idx -> category

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/auth/truelayer/status`);
      setStatus(res.data);
    } catch {
      setStatus({ connected: false });
    }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Handle ?bank_connected=1 / ?bank_error=... returned from OAuth callback
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("bank_connected")) {
      toast.success("Bank account connected");
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus();
    } else if (sp.get("bank_error")) {
      toast.error(`Bank connection failed: ${sp.get("bank_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      const res = await axios.get(`${api}/auth/truelayer/connect`);
      if (res.data?.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start bank connection");
    }
  };

  const handleDisconnect = async (bankId, bankName) => {
    const msg = bankId
      ? `Disconnect ${bankName}? Other connected banks stay. You can reconnect anytime.`
      : "Disconnect ALL connected banks? Tokens will be removed. You can reconnect anytime.";
    if (!window.confirm(msg)) return;
    try {
      if (bankId) {
        await axios.delete(`${api}/auth/truelayer/disconnect/${encodeURIComponent(bankId)}`);
        toast.success(`${bankName} disconnected`);
      } else {
        await axios.delete(`${api}/auth/truelayer/disconnect`);
        toast.success("All banks disconnected");
      }
      setPreview(null);
      await fetchStatus();
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  const handleSync = async (bankId = null) => {
    setSyncing(true);
    setPreview(null);
    try {
      const params = { from_date: syncFrom, to_date: syncTo };
      if (bankId) params.bank_id = bankId;
      const res = await axios.post(`${api}/auth/truelayer/sync`, null, { params });
      setPreview(res.data);
      const sel = {};
      (res.data.transactions || []).forEach((_, i) => { sel[i] = true; });
      setRowSelections(sel);
      setCategoryOverrides({});
      if ((res.data.transactions || []).length === 0) {
        const errMsg = (res.data.errors && res.data.errors.length > 0)
          ? ` — ${res.data.errors[0]}`
          : "";
        toast.info(`No transactions found for ${res.data.from_date} → ${res.data.to_date}${errMsg}`);
      } else {
        const banks = (res.data.banks_synced || []).join(", ");
        toast.success(`Fetched ${res.data.count} transactions from ${banks || "bank"} — review below`);
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  const handleCategoryChange = async (idx, newCat) => {
    setCategoryOverrides((p) => ({ ...p, [idx]: newCat }));
    // Auto-save a rule so this mapping sticks for future syncs
    const txn = preview?.transactions?.[idx];
    if (!txn) return;
    const pattern = derivePattern(txn.description);
    if (!pattern) return;
    try {
      await axios.post(`${api}/auth/truelayer/rules`, { pattern, category: newCat });
      toast.success(`Remembered: "${pattern}" → ${newCat}`, { duration: 2000 });
    } catch {
      // silent — rule save shouldn't block category UI
    }
  };

  const handleImport = async () => {
    if (!preview?.transactions?.length) return;
    // Apply overrides and filter out transfers
    const merged = preview.transactions.map((t, i) => ({
      ...t,
      category: categoryOverrides[i] ?? t.category,
    }));
    const selected = merged.filter((_, i) => rowSelections[i]);
    if (selected.length === 0) {
      toast.error("Select at least one transaction to import");
      return;
    }
    const transfersCount = selected.filter((t) => t.category === "transfer").length;
    setImporting(true);
    try {
      const res = await axios.post(`${api}/auth/truelayer/import`, {
        month: importMonth,
        mode,
        transactions: selected,
      });
      const parts = [`Imported ${res.data.inserted} transactions into ${importMonth}`];
      if (transfersCount > 0) parts.push(`(${transfersCount} transfers excluded)`);
      toast.success(parts.join(" "));
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };

  const selectedCount = useMemo(() => Object.values(rowSelections).filter(Boolean).length, [rowSelections]);

  const toggleAll = (value) => {
    const sel = {};
    (preview?.transactions || []).forEach((_, i) => { sel[i] = value; });
    setRowSelections(sel);
  };

  if (status === null) {
    return (
      <div className="border border-gray-200 rounded-sm p-5">
        <p className="text-sm text-gray-400">Loading bank connection…</p>
      </div>
    );
  }

  return (
    <div data-testid="bank-connection-section" className="border border-gray-200 rounded-sm">
      <div className="border-b border-gray-200 px-5 py-3 flex items-center gap-2">
        <Landmark className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-600">Bank Connection</h2>
        <Badge variant="outline" className="text-[10px] ml-auto">Open Banking · TrueLayer</Badge>
      </div>

      <div className="p-5 space-y-4">
        {!status.connected ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-600">
              Connect your UK bank account via TrueLayer Open Banking to auto-import transactions into your Monthly Budget.
              Read-only access via FCA-regulated consent.
            </p>
            <Button
              onClick={handleConnect}
              className="rounded-sm bg-black text-white hover:bg-gray-800 gap-2 text-xs"
              data-testid="connect-bank-btn"
            >
              <LinkIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
              Connect Bank Account
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Global date range + Add Bank */}
            <div className="flex flex-wrap items-end justify-between gap-3 pb-3 border-b border-gray-100">
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">From</label>
                  <input
                    type="date" value={syncFrom} onChange={(e) => setSyncFrom(e.target.value)} max={syncTo}
                    className="text-xs border border-gray-300 rounded-sm px-2 py-1 font-mono-data"
                    data-testid="sync-from-date"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">To</label>
                  <input
                    type="date" value={syncTo} onChange={(e) => setSyncTo(e.target.value)} min={syncFrom} max={today()}
                    className="text-xs border border-gray-300 rounded-sm px-2 py-1 font-mono-data"
                    data-testid="sync-to-date"
                  />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setSyncFrom(daysAgo(30)); setSyncTo(today()); }}
                    className="text-[10px] px-2 py-1 border border-gray-200 rounded-sm hover:bg-gray-50"
                    data-testid="preset-30d">30d</button>
                  <button onClick={() => { setSyncFrom(daysAgo(90)); setSyncTo(today()); }}
                    className="text-[10px] px-2 py-1 border border-gray-200 rounded-sm hover:bg-gray-50"
                    data-testid="preset-90d">90d</button>
                  <button onClick={() => { setSyncFrom(daysAgo(180)); setSyncTo(today()); }}
                    className="text-[10px] px-2 py-1 border border-gray-200 rounded-sm hover:bg-gray-50"
                    data-testid="preset-180d">180d</button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleSync(null)}
                  disabled={syncing}
                  className="rounded-sm bg-black text-white hover:bg-gray-800 gap-1.5 text-xs"
                  data-testid="sync-all-banks-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  {syncing ? "Syncing…" : "Sync All Banks"}
                </Button>
                <Button
                  onClick={handleConnect}
                  variant="outline"
                  className="rounded-sm gap-1.5 text-xs"
                  data-testid="connect-another-bank-btn"
                >
                  <LinkIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Add Bank
                </Button>
              </div>
            </div>

            {/* Per-bank cards */}
            <div className="space-y-2" data-testid="banks-list">
              {(status.banks || []).map((bank) => {
                const cStyle = consentStyles[bank.consent_status] || consentStyles.active;
                const isExpired = bank.consent_status === "expired";
                return (
                  <div
                    key={bank.id}
                    data-testid={`bank-card-${bank.id}`}
                    className={`border rounded-sm p-3 flex flex-wrap items-start gap-3 ${cStyle.border} ${isExpired ? cStyle.bg : ""}`}
                  >
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <CheckCircle2 className={`w-4 h-4 ${isExpired ? "text-red-600" : "text-green-600"}`} strokeWidth={1.5} />
                        <span className="text-sm font-semibold text-gray-900" data-testid={`bank-name-${bank.id}`}>
                          {bank.provider_name || "Bank"}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${cStyle.text} ${cStyle.border} ${cStyle.bg}`}
                          data-testid={`bank-${bank.id}-consent`}
                        >
                          {cStyle.label}
                          {bank.consent_days_remaining !== null && bank.consent_days_remaining !== undefined && !isExpired && (
                            <span className="ml-1 font-mono-data font-semibold">· {bank.consent_days_remaining}d</span>
                          )}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-gray-500">
                        Connected: {formatDateTime(bank.connected_at)} · Last sync: {formatDateTime(bank.last_sync_at)}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Consent expires: <strong className={isExpired ? "text-red-700" : cStyle.text}>{formatDateOnly(bank.consent_expires_at)}</strong>
                        {bank.last_refresh_at && <> · Last refresh: {formatDateTime(bank.last_refresh_at)}</>}
                      </p>
                      {isExpired && (
                        <p className="text-[11px] text-red-700 mt-1 font-semibold">
                          Your 90-day consent has expired. Click Reconnect below to restore access.
                        </p>
                      )}
                      {bank.accounts && bank.accounts.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {bank.accounts.map((a) => (
                            <Badge
                              key={a.id}
                              variant="outline"
                              className="text-[10px] font-mono-data border-gray-300 bg-gray-50"
                              data-testid={`bank-${bank.id}-account-${a.id}`}
                            >
                              {a.kind === "card" ? "💳" : "🏦"} {a.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      {isExpired ? (
                        <Button
                          onClick={handleConnect}
                          size="sm"
                          className="rounded-sm gap-1.5 text-[11px] h-7 px-2 bg-black text-white hover:bg-gray-800"
                          data-testid={`reconnect-bank-${bank.id}-btn`}
                        >
                          <LinkIcon className="w-3 h-3" strokeWidth={1.5} />
                          Reconnect
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleSync(bank.id)}
                          disabled={syncing}
                          variant="outline"
                          size="sm"
                          className="rounded-sm gap-1.5 text-[11px] h-7 px-2"
                          data-testid={`sync-bank-${bank.id}-btn`}
                        >
                          <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.5} />
                          Sync
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDisconnect(bank.id, bank.provider_name)}
                        variant="outline"
                        size="sm"
                        className="rounded-sm gap-1.5 text-[11px] h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`disconnect-bank-${bank.id}-btn`}
                      >
                        <Unlink className="w-3 h-3" strokeWidth={1.5} />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Preview table */}
            {preview && preview.transactions && preview.transactions.length > 0 && (
              <div className="border border-gray-200 rounded-sm">
                <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-700">
                    Preview — {preview.count} transactions from {(preview.banks_synced || []).join(", ") || preview.provider_name || "bank"}
                  </span>
                  <span className="text-[10px] text-gray-400">({preview.from_date} → {preview.to_date})</span>
                  <div className="ml-auto flex gap-2">
                    <button
                      onClick={() => toggleAll(true)}
                      className="text-[10px] text-blue-600 underline"
                      data-testid="preview-select-all"
                    >Select all</button>
                    <button
                      onClick={() => toggleAll(false)}
                      className="text-[10px] text-gray-500 underline"
                      data-testid="preview-select-none"
                    >None</button>
                  </div>
                </div>
                {preview.errors && preview.errors.length > 0 && (
                  <div className="px-3 py-2 border-b border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-amber-600" strokeWidth={1.5} />
                      <span className="text-[10px] text-amber-800">{preview.errors.length} warnings during sync</span>
                    </div>
                  </div>
                )}
                <div className="max-h-[320px] overflow-auto">
                  <table className="spreadsheet-table w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="w-8"></th>
                        <th
                          className="text-left cursor-pointer select-none hover:bg-gray-100"
                          style={{ width: 90 }}
                          onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                          data-testid="preview-date-sort"
                          title="Click to toggle date sort"
                        >
                          Date {sortDir === "desc" ? "↓" : "↑"}
                        </th>
                        <th className="text-left">Description</th>
                        <th className="text-left" style={{ width: 110 }}>Bank</th>
                        <th className="text-center" style={{ width: 150 }}>Category</th>
                        <th className="text-right" style={{ width: 90 }}>Amount</th>
                        <th className="text-left" style={{ width: 110 }}>Debt Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...preview.transactions.map((t, i) => ({ t, i }))]
                        .sort((a, b) => {
                          const cmp = (a.t.date || "").localeCompare(b.t.date || "");
                          return sortDir === "desc" ? -cmp : cmp;
                        })
                        .map(({ t, i }) => {
                          const cat = categoryOverrides[i] ?? t.category;
                          const isTransfer = cat === "transfer";
                          return (
                            <tr
                              key={i}
                              data-testid={`preview-row-${i}`}
                              className={isTransfer ? "opacity-50" : ""}
                            >
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={!!rowSelections[i]}
                                  onChange={(e) => setRowSelections((s) => ({ ...s, [i]: e.target.checked }))}
                                />
                              </td>
                              <td className="font-mono-data text-gray-600">{t.date}</td>
                              <td className={isTransfer ? "line-through" : ""}>{t.description}</td>
                              <td className="text-[10px] text-gray-500 font-mono-data">{t._bank_name || "-"}</td>
                              <td className="text-center">
                                <select
                                  value={cat}
                                  onChange={(e) => handleCategoryChange(i, e.target.value)}
                                  className={`text-[10px] font-mono-data border rounded-sm px-1.5 py-0.5 w-full max-w-[140px] ${categoryBadge[cat] || categoryBadge.expense}`}
                                  data-testid={`preview-category-${i}`}
                                >
                                  {CATEGORY_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="numeric">{t.actual > 0 ? fmt(t.actual) : "-"}</td>
                              <td className="text-xs">
                                {t.debt_name ? (
                                  <span className="text-blue-600 font-medium">{t.debt_name}</span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                {/* Import controls */}
                <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-600">Target month</span>
                    <input
                      type="month"
                      value={importMonth}
                      onChange={(e) => setImportMonth(e.target.value)}
                      className="text-xs border border-gray-300 rounded-sm px-2 py-1 font-mono-data"
                      data-testid="import-month-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-[11px] text-gray-600">
                      <input
                        type="radio"
                        name="tl-mode"
                        checked={mode === "append"}
                        onChange={() => setMode("append")}
                      /> Append
                    </label>
                    <label className="flex items-center gap-1 text-[11px] text-gray-600">
                      <input
                        type="radio"
                        name="tl-mode"
                        checked={mode === "replace"}
                        onChange={() => setMode("replace")}
                      /> Replace
                    </label>
                  </div>
                  <span className="text-[10px] text-gray-400 ml-auto">{selectedCount} selected</span>
                  <Button
                    onClick={handleImport}
                    disabled={importing || selectedCount === 0}
                    className={`rounded-sm text-xs gap-1.5 ${mode === "replace" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-black hover:bg-gray-800 text-white"}`}
                    data-testid="bank-import-confirm"
                  >
                    <DownloadIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {importing ? "Importing…" : mode === "replace" ? "Replace & Import" : "Import"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
